import { useEffect, useState } from "react";
import { Pencil, Trash2, AlertCircle, RefreshCw } from "lucide-react";
import { addOffer, deleteOffer, getOffer, updateOffer, uploadImage } from "../service/api";
import { toast } from "sonner";
import { format, parseISO, isValid } from "date-fns";
import { DatePicker } from "../components/ui/date-picker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const Offer = () => {
    const [image, setImage] = useState<any>(null);
    const [errors, setErrors] = useState<any>({});
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deletedId, setDeletedId] = useState<any>();
    const [editId, setEditId] = useState<any>(null);
    const [formValues, setFormValues] = useState({
        type: '',
        code: '',
        discount: '',
        expiryDate: ''
    });

    useEffect(() => {
        getAllOffer();
    }, []);

    const getAllOffer = async () => {
        setLoading(true);
        try {
            let res = await getOffer();
            setOffers(res.data.data);
        } finally {
            setLoading(false);
        }
    }

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };

    const handleImageChange = async (e: any) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file, file.name);
        try {
            const response = await uploadImage(formData);
            setImage(response.data.files[0]);
            setErrors({ ...errors, image: "" });
        } catch (error) {

            toast.error("Image upload failed");
        }
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        if (!formValues.type.trim()) newErrors.type = "Type is required";
        if (!formValues.code.trim()) newErrors.code = "Code is required";
        if (!formValues.discount.trim()) newErrors.discount = "Discount is required";
        if (!formValues.expiryDate.trim()) newErrors.expiryDate = "Expiry date is required";
        if (!image || (typeof image === 'object' && image.length === 0)) newErrors.image = "Image is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEdit = (item: any) => {
        setFormValues({
            type: item.type,
            code: item.code,
            discount: item.discount,
            expiryDate: item.expiry_date,
        });
        setImage(item.image);
        setEditId(item.id);
    };

    const handleSubmit = async (event: any) => {
        event.preventDefault();
        if (!validateForm()) return;
        try {
            const body = {
                type: formValues.type,
                code: formValues.code,
                discount: formValues.discount,
                expiry_date: formValues.expiryDate,
                image: image,
            };
            if (editId) {
                await updateOffer({ ...body, id: editId });
                toast.success("Offer updated");
            } else {
                await addOffer(body);
                toast.success("Offer created");
            }
            setErrors({});
            setEditId(null);
            setFormValues({ type: '', code: '', discount: '', expiryDate: '' });
            setImage(null);
            getAllOffer();
        } catch (err) {
            toast.error("Failed to save offer");
        }
    };

    const handleDelete = (data: any) => {
        setDeleteModal(true);
        setDeletedId(data.id);
    };

    const deleteSelectedOffer = async () => {
        try {
            await deleteOffer({ id: deletedId });
            toast.success("Offer deleted");
            setDeleteModal(false);
            getAllOffer();
        } catch (err) {
            toast.error("Failed to delete offer");
        }
    };

    return (
        <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Offers</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Promo codes and discounts. Create or edit below; the list updates after save.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => getAllOffer()}>
                    <RefreshCw className="h-4 w-4" />
                </Button>
            </div>

            {/* Form card */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">{editId ? "Edit offer" : "New offer"}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="offer-type">Type</Label>
                            <Input
                                id="offer-type"
                                type="text"
                                name="type"
                                placeholder="e.g. Partner"
                                value={formValues.type}
                                onChange={handleInputChange}
                            />
                            {errors.type && <p className="text-destructive text-xs">{errors.type}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="offer-code">Code</Label>
                            <Input
                                id="offer-code"
                                type="text"
                                name="code"
                                placeholder="Promo code"
                                value={formValues.code}
                                onChange={handleInputChange}
                            />
                            {errors.code && <p className="text-destructive text-xs">{errors.code}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="offer-discount">Discount</Label>
                            <Input
                                id="offer-discount"
                                type="text"
                                name="discount"
                                placeholder="e.g. 10%"
                                value={formValues.discount}
                                onChange={handleInputChange}
                            />
                            {errors.discount && <p className="text-destructive text-xs">{errors.discount}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Expiry date</Label>
                            <DatePicker
                                value={formValues.expiryDate && isValid(parseISO(formValues.expiryDate)) ? parseISO(formValues.expiryDate) : undefined}
                                onChange={(date) => {
                                    setFormValues({ ...formValues, expiryDate: date ? format(date, 'yyyy-MM-dd') : '' });
                                    setErrors({ ...errors, expiryDate: "" });
                                }}
                                className="w-full"
                            />
                            {errors.expiryDate && <p className="text-destructive text-xs">{errors.expiryDate}</p>}
                        </div>
                        <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
                            <Label>Image</Label>
                            <input
                                type="file"
                                onChange={handleImageChange}
                                className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
                            />
                            {errors.image && <p className="text-destructive text-xs">{errors.image}</p>}
                        </div>
                    </div>
                    <div className="pt-2 border-t flex flex-wrap gap-2">
                        <Button size="sm" onClick={handleSubmit}>
                            {editId ? "Update offer" : "Create offer"}
                        </Button>
                        {editId && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                    setEditId(null);
                                    setFormValues({ type: "", code: "", discount: "", expiryDate: "" });
                                    setImage(null);
                                }}
                            >
                                Cancel edit
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Table card */}
            <Card className="overflow-hidden">
                {loading ? (
                    <div className="flex min-h-[200px] items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>Image</TableHead>
                                    <TableHead>Expires</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Code</TableHead>
                                    <TableHead>Discount</TableHead>
                                    <TableHead className="text-right w-24">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {offers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center text-muted-foreground py-14">
                                            No offers yet. Create one using the form above.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    offers.map((item: any, index: number) => {
                                        const formattedDate = format(new Date(item.expiry_date), "dd-MM-yyyy");
                                        return (
                                            <TableRow key={item.id}>
                                                <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                                                <TableCell>
                                                    <img
                                                        src={item.image}
                                                        alt=""
                                                        className="h-12 w-20 object-cover rounded-md border"
                                                        onError={(e) => { e.currentTarget.src = "/wood-blog-placeholder.jpg"; }}
                                                    />
                                                </TableCell>
                                                <TableCell className="text-sm tabular-nums">{formattedDate}</TableCell>
                                                <TableCell>{item.type}</TableCell>
                                                <TableCell className="font-mono text-sm">{item.code}</TableCell>
                                                <TableCell>{item.discount}</TableCell>
                                                <TableCell className="text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        onClick={() => handleEdit(item)}
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-destructive hover:text-destructive"
                                                        onClick={() => handleDelete(item)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Card>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader className="items-center text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
                        <DialogTitle>Delete this offer?</DialogTitle>
                        <DialogDescription>This cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-row justify-center gap-2 sm:justify-center">
                        <Button variant="outline" onClick={() => setDeleteModal(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={deleteSelectedOffer}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default Offer;
